import { useEffect, useState } from "react";
import api, { formatError, downloadReport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleOf } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AiReport from "@/components/AiReport";
import { ListToolbar } from "@/components/ListToolbar";
import { Plus, Loader2, FileText, RefreshCw, Upload, Download, ShieldAlert, FileDown } from "lucide-react";

const STATUS = {
  analyzing: { c: "bg-blue-100 text-blue-800", l: "AI Analyzing" },
  pending_review: { c: "bg-amber-100 text-amber-800", l: "Pending Human Review" },
  approved: { c: "bg-emerald-100 text-emerald-800", l: "Approved" },
  revision_requested: { c: "bg-orange-100 text-orange-800", l: "Revise & Resubmit" },
  declined: { c: "bg-red-100 text-red-800", l: "Declined" },
};

export default function ConceptPapers() {
  const { user } = useAuth();
  const role = roleOf(user);
  const isStudent = role === "student";
  const canReview = ["supervisor", "tenant_admin", "super_admin"].includes(role);
  const [papers, setPapers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: "", programme: "", content: "" });
  const [file, setFile] = useState(null);
  const [decision, setDecision] = useState("");
  const [notes, setNotes] = useState("");
  const [emailReport, setEmailReport] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => api.get("/concept-papers").then((r) => setPapers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!papers.some((p) => p.status === "analyzing")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [papers]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.title || !form.programme || (!file && !form.content.trim())) {
      toast.error("Add a title, programme, and either upload a file or paste text."); return;
    }
    setSubmitting(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("title", form.title); fd.append("programme", form.programme); fd.append("file", file);
        await api.post("/concept-papers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/concept-papers", form);
      }
      toast.success("Submitted — AI diagnostic in progress");
      setOpen(false);
      setForm({ title: "", programme: "", content: "" }); setFile(null);
      setTimeout(load, 1500);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const openDetail = async (p) => {
    const { data } = await api.get(`/concept-papers/${p.id}`);
    setSelected(data);
    setDecision(data.ai_report?.advisory_recommendation || "");
    setNotes("");
    setEmailReport(false);
  };

  const review = async () => {
    try {
      await api.patch(`/concept-papers/${selected.id}/review`, { decision, notes, email_report: emailReport });
      toast.success("Decision recorded");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const reanalyze = async () => {
    try {
      const { data } = await api.post(`/concept-papers/${selected.id}/reanalyze`);
      setSelected({ ...selected, ai_report: data.ai_report });
      toast.success("Re-analyzed");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Concept-Paper Gateway</h1>
          <p className="text-muted-foreground">AI prepares a rubric diagnostic; the reviewer decides.</p>
        </div>
        {isStudent && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-concept-btn" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-1" /> Submit Concept Paper</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl">
              <DialogHeader><DialogTitle>Submit Concept Paper</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input data-testid="cp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>Programme</Label><Input data-testid="cp-programme" value={form.programme} onChange={(e) => setForm({ ...form, programme: e.target.value })} /></div>
                <div>
                  <Label>Upload document (PDF, Word .docx, or text)</Label>
                  <input data-testid="cp-file" type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[rgb(var(--brand))] file:text-white file:px-3 file:py-1.5 file:text-sm cursor-pointer" />
                  {file && <p className="text-xs text-muted-foreground mt-1">Selected: {file.name}</p>}
                </div>
                <div className="text-center text-xs text-muted-foreground">— or —</div>
                <div><Label>Paste text {file ? "(optional)" : ""}</Label><Textarea data-testid="cp-content" rows={7} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Paste your concept paper (problem, objectives, methodology, references)…" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="cp-submit" onClick={submit} disabled={submitting} className="brand-bg text-white">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for AI Diagnostic"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-3" data-testid="concept-list">
        {!isStudent && (
          <ListToolbar
            testid="concept"
            search={q}
            onSearch={setQ}
            placeholder="Search by title or student…"
            filters={[{
              key: "status", value: statusFilter, onChange: setStatusFilter, label: "Status",
              options: [{ value: "all", label: "All statuses" }, ...Object.entries(STATUS).map(([v, o]) => ({ value: v, label: o.l }))],
            }]}
          />
        )}
        {papers
          .filter((p) => statusFilter === "all" || p.status === statusFilter)
          .filter((p) => {
            const t = q.trim().toLowerCase();
            return !t || (p.title || "").toLowerCase().includes(t) || (p.student_name || "").toLowerCase().includes(t);
          })
          .map((p) => {
          const s = STATUS[p.status] || { c: "bg-slate-100", l: p.status };
          return (
            <Card key={p.id} className="p-5 flex items-center justify-between gap-4 hover:shadow-sm cursor-pointer" data-testid={`concept-${p.id}`} onClick={() => openDetail(p)}>
              <div className="flex items-start gap-3 min-w-0">
                <FileText className="h-5 w-5 brand-text mt-1 shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold truncate">{p.title}</h3>
                  <p className="text-sm text-muted-foreground">{p.programme} · {p.student_name}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={s.c}>{s.l}</Badge>
                {!isStudent && p.ai_report?.integrity_signals?.length > 0 && (
                  <Badge className="bg-red-100 text-red-700 gap-1" data-testid={`integrity-flag-${p.id}`}><ShieldAlert className="h-3 w-3" />{p.ai_report.integrity_signals.length} integrity</Badge>
                )}
              </div>
            </Card>
          );
        })}
        {papers.length === 0 && <p className="text-muted-foreground text-sm">No concept papers yet.</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8">{selected.title}</DialogTitle>
                <p className="text-sm text-muted-foreground">{selected.programme} · {selected.student_name}</p>
              </DialogHeader>

              {selected.file?.file_id && (
                <a data-testid="cp-download" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${selected.file.file_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm brand-text hover:underline">
                  <Download className="h-4 w-4" /> Download original ({selected.file.original_filename})
                </a>
              )}

              {canReview && selected.ai_report && (
                <button data-testid="cp-export-pdf" onClick={async () => {
                  try { await downloadReport(`/concept-papers/${selected.id}/report.pdf`, `integrity-report-${selected.title}.pdf`); }
                  catch (e) { toast.error("Could not generate the report PDF. Please try again."); }
                }} className="inline-flex w-fit items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-secondary transition-colors">
                  <FileDown className="h-4 w-4 brand-text" /> Export integrity & AI report (PDF)
                </button>
              )}

              {isStudent && !selected.reviewer_decision ? (
                <div className="rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="student-under-review">
                  Your concept paper has been received. The AI assistant has prepared a diagnostic for your reviewer — you'll see the outcome and guidance once a human reviewer releases their decision.
                </div>
              ) : (
                <AiReport report={selected.ai_report} error={selected.ai_error} />
              )}

              {selected.reviewer_decision ? (
                <div className="rounded-md border border-border bg-secondary/40 p-4">
                  <p className="text-sm font-medium">Human decision: <span className="capitalize">{selected.reviewer_decision.replaceAll("_", " ")}</span></p>
                  <p className="text-sm text-muted-foreground">{selected.decision_notes}</p>
                  <p className="text-xs text-muted-foreground mt-1">by {selected.reviewed_by}</p>
                </div>
              ) : canReview ? (
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <p className="font-heading font-semibold text-sm">Reviewer decision (human-in-the-loop)</p>
                  <div className="flex gap-2 flex-wrap">
                    {["approve", "revise_and_resubmit", "decline"].map((d) => (
                      <button key={d} data-testid={`decision-${d}`} onClick={() => setDecision(d)}
                        className={`px-3 py-1.5 rounded-md text-sm border ${decision === d ? "brand-bg text-white brand-border" : "border-border hover:bg-secondary"}`}>
                        {d.replaceAll("_", " ")}
                      </button>
                    ))}
                    <Button size="sm" variant="ghost" data-testid="reanalyze-btn" onClick={reanalyze}><RefreshCw className="h-4 w-4 mr-1" />Re-analyze</Button>
                  </div>
                  <Textarea data-testid="review-notes" placeholder="Decision notes to the student…" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input data-testid="cp-email-report" type="checkbox" checked={emailReport} onChange={(e) => setEmailReport(e.target.checked)} className="h-4 w-4 accent-[rgb(var(--brand))]" />
                    Email the integrity &amp; AI report (PDF) to the student
                  </label>
                  <Button data-testid="submit-review" onClick={review} disabled={!decision || !notes} className="brand-bg text-white">Record Decision</Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Awaiting reviewer decision.</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
